<script lang="ts" setup>
    import {onMounted, computed, ref} from "vue";
    import DCarouselSlide from "@songfinch/design-system/src/atoms/DCarouselSlide.vue";

    import DStat from "@songfinch/design-system/src/molecules/DStat.vue";

    import DContainer from "@songfinch/design-system/src/atoms/DContainer.vue";
    import DArtistCard from "@songfinch/design-system/src/cards/DArtistCard.vue";
    import DProductCard from "@songfinch/design-system/src/cards/DProductCard.vue";
    import DReviewCard from "@songfinch/design-system/src/cards/DReviewCard.vue";
    import DPressReviewCard from "@songfinch/design-system/src/cards/DPressReviewCard.vue";
    import DRevealCard from "@songfinch/design-system/src/cards/DRevealCard.vue";

    import DHeroMediaBackgroundBlock from "@songfinch/design-system/src/blocks/DHeroMediaBackgroundBlock.vue";
    import DHomeProductBlock from "@songfinch/design-system/src/blocks/DHomeProductBlock.vue";
    import DCardDisplayGridBlock from "@songfinch/design-system/src/blocks/DCardDisplayGridBlock.vue";
    import DCarouselBlock from "@songfinch/design-system/src/blocks/DCarouselBlock.vue";
    import DFaqBlock from "@songfinch/design-system/src/blocks/DFaqBlock.vue";


    import {filterProductsByIds} from "@songfinch/utils/src/map";
    import {normalizeArtistCard, normalizeCMSCta, normalizeCMSMedia} from "@songfinch/utils/src/normalizers";

    import {reviewCards} from "./HomePage";
    import {useStore} from "vuex";

    import {fetchProducts} from "@songfinch/data-source/products";
    import {fetchArtists} from "@songfinch/data-source/artists";

    import type {ArtistListType, ArtistCard, ProductListItem} from "@songfinch/types";


    const store = useStore();
    const artists = ref<ArtistCard[]>();
    const products = ref<ProductListItem[]>();

    const pageData = computed(() => store.state.cms.pageData);

    const filteredProducts = computed<ProductListItem[] | undefined>(() => {
        if (!products.value) return undefined;
        if (!pageData.value?.addOnsSection?.products) return products.value;

        const sortOrder = pageData.value.addOnsSection.products.reduce((sortOrder: string[], product: {product: {
            text: string,
            id: string
        }}) => {
            if (product?.product?.id) {
                sortOrder.push(product.product.id);
            }
            return sortOrder;
        }, []);
        return filterProductsByIds(products.value, sortOrder);
    });

    const getFeaturedArtists = async () => {
        if (pageData.value?.artistsSection?.artists?.length) {
            const artistListIds = pageData.value?.artistsSection.artists.reduce((artistIdList: string[], artist: {artist: {id: string}}) => {
                if (artist?.artist?.id) {
                    artistIdList.push(artist?.artist?.id);
                }
                return artistIdList;
            }, []);
            
            if (!artistListIds.length) return;

            const artistRes = await fetchArtists({ids: artistListIds, show_at_capacity: true, per_page: 10});

            artists.value = artistRes.artists.reduce((artistCards: ArtistCard[], artist: ArtistListType) => {
                const card = normalizeArtistCard(artist);
                if (card) {
                    artistCards.push(card);
                }
                return artistCards;
            }, []);
        }
    };

    onMounted(async () => {
        const productRes = await fetchProducts({full: true});
        products.value = productRes;
        getFeaturedArtists();
    });
</script>

<template>
    <DContainer>
        <DHeroMediaBackgroundBlock
            v-if="pageData?.heroSection"
            data-test-id="homepage-hero"
            :headline="pageData.heroSection.headline"
            :sub-headline="pageData.heroSection.subHeadline"
            :media="normalizeCMSMedia(pageData.heroSection.media)"
            :mobileMedia="normalizeCMSMedia(pageData.heroSection.mobileMedia)"
            :cta="normalizeCMSCta(pageData.heroSection.cta)"
        />

        <DHomeProductBlock
            v-if="pageData?.featuredProductSection"
            dataTestId="homepage-primary-product-section"
            v-bind="pageData.featuredProductSection"
            :cta="normalizeCMSCta(pageData.featuredProductSection.cta)"
            :secondaryCta="normalizeCMSCta(pageData.featuredProductSection.secondaryCta)"
            :image="normalizeCMSMedia(pageData.featuredProductSection.media)"
            theme="dark"
            order="reversed"
        />

        <DCardDisplayGridBlock
            v-if="pageData?.statsSection?.stats"
            dataTestId="homepage-stat-section"
            :columns="4"
            theme="light"
        >
            <template v-for="content in pageData.statsSection.stats" :key="content.value">
                <DStat :value="content.value" :description="content.description"/>
            </template>
        </DCardDisplayGridBlock>
        
        <DCarouselBlock
            v-if="pageData?.artistsSection"
            dataTestId="homepage-artists-section"
            theme="dark"
            v-bind="pageData.artistsSection"
            :cta="normalizeCMSCta(pageData.artistsSection.cta)"
        >
            <template v-if="artists" #carousel>
                <DCarouselSlide v-for="(artist, i) in artists" :key="i">
                    <DArtistCard
                        v-if="artist.id"
                        class="homepage-artist-card"
                        :dataTestId="`homepage-artists-card-${i}`"
                        :showSong="true"
                        :artist="artist"
                    />
                </DCarouselSlide>
            </template>
        </DCarouselBlock>

        <DCarouselBlock
            v-if="pageData?.reviewsSection"
            v-bind="pageData.reviewsSection"
            dataTestId="homepage-reviews-section"
            theme="light"
        >
            <template v-if="reviewCards" #carousel>
                <DCarouselSlide v-for="(review, i) in reviewCards" :key="i">
                    <DReviewCard
                        v-if="review.contentTypeId === 'review'"
                        theme="light"
                        :dataTestId="`homepage-review-card-${i}`"
                        v-bind="review"
                    />
                    <DPressReviewCard
                        v-if="review.contentTypeId === 'pressReview'"
                        theme="light"
                        :dataTestId="`homepage-review-card-${i}`"
                        v-bind="review"
                    />
                    <DRevealCard
                        v-else-if="review.contentTypeId === 'reveal'"
                        theme="dark"
                        :dataTestId="`homepage-reveal-card-${i}`"
                        v-bind="review"
                    />
                </DCarouselSlide>
            </template>
        </DCarouselBlock>

        <DHomeProductBlock
            v-if="pageData?.secondaryProductSection"
            dataTestId="homepage-secondary-product-section"
            v-bind="pageData.secondaryProductSection"
            :cta="normalizeCMSCta(pageData.secondaryProductSection.cta)"
            :secondaryCta="normalizeCMSCta(pageData.secondaryProductSection.secondaryCta)"
            :image="normalizeCMSMedia(pageData.secondaryProductSection.media)"
            theme="dark"
        />

        <DCarouselBlock
            v-if="pageData?.addOnsSection"
            theme="white"
            dataTestId="homepage-addons-section"
            :bodyTop="true"
            v-bind="pageData.addOnsSection"
            :cta="normalizeCMSCta(pageData.addOnsSection.cta)"
        >
            <template v-if="filteredProducts" #carousel>
                <DCarouselSlide v-for="(product, i) in filteredProducts" :key="i">
                    <DProductCard
                        :dataTestId="`homepage-products-card-${i}`"
                        :product="product"
                        :showButton="true"
                    />
                </DCarouselSlide>
            </template>
        </DCarouselBlock>

        <DFaqBlock
            v-if="pageData?.faqSection"
            dataTestId="homepage"
            :pageData="pageData?.faqSection"
            theme="light"
        />
    </DContainer>
</template>

<style lang="scss">
.homepage-artist-card {
    height: 333px;
    width: 333px;
}
</style>
